import React, { useState, useLayoutEffect, useMemo } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../hooks/useScreenSize";
import { getAmChartsLocale, translateMonthLabels } from "../../../utils/chartDataProcessors";
import { useLanguage } from "../../../context/LanguageContext";

const CreatedOrdersByEmployee = ({ item }) => {
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();

  const employeeColors = [
    "#4064CA",
    "#4E7CFF",
    "#22C0FF",
    "#FFB5CE",
    "#FF6B9D",
    "#C77DFF",
    "#7209B7",
    "#560BAD",
    "#480CA8",
    "#3A0CA3",
    "#3F37C9",
    "#4361EE",
    "#4895EF",
    "#4CC9F0",
  ];

  const processEmployeesData = (data) => {
    if (!data || !Array.isArray(data)) return { processedData: [], uniqueEmployees: [] };

    const employeesSet = new Set();
    data.forEach((item) => {
      if (item.employees && Array.isArray(item.employees)) {
        item.employees.forEach((emp) => {
          employeesSet.add(emp.name);
        });
      }
    });
    const uniqueEmployees = Array.from(employeesSet);

    const processedData = data.map((item) => {
      const monthData = {
        month: item.month || "",
      };

      uniqueEmployees.forEach((empName) => {
        monthData[empName] = 0;
      });

      if (item.employees && Array.isArray(item.employees)) {
        item.employees.forEach((emp) => {
          monthData[emp.name] = emp.count || 0;
        });
      }

      return monthData;
    });

    return { processedData, uniqueEmployees };
  };

  const { processedData, uniqueEmployees } = useMemo(() => {
    const result = processEmployeesData(item);
    return {
      processedData: translateMonthLabels(result.processedData, currentLanguage),
      uniqueEmployees: result.uniqueEmployees,
    };
  }, [item, currentLanguage]);

  useLayoutEffect(() => {
    let root = am5.Root.new("chartdiv-client-employees-orders");
    root.setThemes([am5themes_Animated.new(root)]);

    const locale = getAmChartsLocale(currentLanguage);
    if (locale) {
      root.locale = locale;
    }

    root._logo.dispose();

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        wheelY: "zoomX",
      }),
    );

    if (isMobileView) {
      chart.set("paddingLeft", 1);
    }
    chart.set("wheelable", true);

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "month",
        groupData: true,
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 30,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      }),
    );
    xAxis.data.setAll(processedData);

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomX",
        xAxis: xAxis,
      }),
    );
    cursor.lineY.set("visible", false);

    xAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    xAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
      rotation: 0,
      multiline: true,
      textAlign: "center",
      centerX: am5.percent(50),
      maxWidth: 50,
      oversizedBehavior: "wrap"
    });

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      }),
    );

    yAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
    });

    yAxis.children.unshift(
      am5.Label.new(root, {
        rotation: -90,
        text: t("analytics.requestCount"),
        y: am5.p50,
        centerX: am5.p50,
        centerY: am5.p50,
        fontSize: 14,
        fill: am5.color("#5A5959"),
      }),
    );

    uniqueEmployees.forEach((employeeName, index) => {
      const color = employeeColors[index % employeeColors.length];

      let series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: employeeName,
          xAxis: xAxis,
          yAxis: yAxis,
          categoryXField: "month",
          valueYField: employeeName,
          stacked: true,
        }),
      );

      series.columns.template.setAll({
        fill: am5.color(color),
        stroke: am5.color(color),
        maxWidth: 80,
      });

      series.set(
        "tooltip",
        am5.Tooltip.new(root, {
          labelText: "{name}: {valueY}",
        }),
      );

      series.data.setAll(processedData);
    });

    return () => {
      root.dispose();
    };
  }, [item, isMobileView, processedData, uniqueEmployees, currentLanguage]);

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>{t("analytics.requestsByEmployeePerMonth")}</span>
      </div>

      <div className={cn(style.area__namesMobile)}>
        {uniqueEmployees.map((employeeName, index) => (
          <div key={employeeName} style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div
              className={style.area_icon}
              style={{ backgroundColor: employeeColors[index % employeeColors.length] }}
            ></div>
            <div className={style.area_type}>{employeeName}</div>
          </div>
        ))}
      </div>

      <div className={style.area}>
        <div id="chartdiv-client-employees-orders" className={style.area_chart}></div>
        <div className={style.area_names}>
          {uniqueEmployees.map((employeeName, index) => (
            <div key={employeeName} style={{ display: "flex", gap: "4px", alignItems: "center" }}>
              <div
                className={style.area_icon}
                style={{ backgroundColor: employeeColors[index % employeeColors.length] }}
              ></div>
              <div className={style.area_type}>{employeeName}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CreatedOrdersByEmployee;
